import { supabase } from "../supabaseClient";
import { resolveImageUrl } from "./mediaUrl";

/**
 * Fetch and normalize the current user's collection.
 *
 * This is the single normalization boundary for collection items. Components
 * should consume the shape returned here instead of reaching into raw
 * user_items / items / item_images rows.
 */
export const fetchUserCards = async (userId) => {
  if (!userId) return [];

  try {
    const [categoriesResult, subcategoriesResult, userItemsResult] =
      await Promise.all([
        supabase.from("categories").select("id, name"),
        supabase.from("subcategories").select("id, name"),
        supabase
          .from("user_items")
          .select(`
            *,
            item:items(*),
            images:item_images!fk_user_item_image(*),
            user_item_variants (
              variant_id,
              variant:variants (
                id,
                name
              )
            ),
            user_variant_suggestions (
              id,
              name,
              status
            )
          `)
          .eq("user_id", userId),
      ]);

    if (categoriesResult.error) throw categoriesResult.error;
    if (subcategoriesResult.error) throw subcategoriesResult.error;
    if (userItemsResult.error) throw userItemsResult.error;

    const categoriesMap = Object.fromEntries(
      (categoriesResult.data || []).map((category) => [category.id, category.name])
    );
    const subcategoriesMap = Object.fromEntries(
      (subcategoriesResult.data || []).map((subcategory) => [
        subcategory.id,
        subcategory.name,
      ])
    );

    const userItems = userItemsResult.data || [];
    const userItemIds = userItems.map((item) => item.id).filter(Boolean);

    let submissions = [];
    if (userItemIds.length > 0) {
      const { data, error } = await supabase
        .from("collection_submissions")
        .select("collection_id, user_item_id")
        .in("user_item_id", userItemIds);

      if (error) throw error;
      submissions = data || [];
    }

    const collectionIdsByItem = submissions.reduce((map, submission) => {
      const key = String(submission.user_item_id);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(submission.collection_id);
      return map;
    }, new Map());

    return userItems.map((row) => {
      const canonical = row.item || {};
      const attributes = row.attributes || {};
      // item_id is the identity relationship. Status is moderation/workflow
      // metadata and should not decide which identity source is authoritative.
      const hasCanonicalItem = Boolean(row.item_id && row.item);

      const pick = (field, fallback = "Unknown") => {
        if (field === "year") {
          const value = hasCanonicalItem
            ? canonical.year ?? 0
            : row.user_specific_year ?? row.year ?? 0;
          return Number(value || 0);
        }

        if (field === "year_end") {
          const value = hasCanonicalItem
            ? canonical.year_end ?? null
            : row.user_specific_year_end ?? row.year_end ?? null;
          return value === null || value === "" ? null : Number(value);
        }

        if (hasCanonicalItem) {
          return canonical[field] ?? fallback;
        }

        return row[`user_specific_${field}`] ?? row[field] ?? fallback;
      };

      const images = [...(row.images || [])]
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map((image) => ({
        id: image.id,
        image_url: resolveImageUrl(image),
        position: image.position ?? 0,

        // Storage-neutral image identity
        storage_provider: image.storage_provider ?? null,
        storage_bucket: image.storage_bucket ?? null,
        object_key: image.object_key ?? null,

        // File metadata
        mime_type: image.mime_type ?? null,
        file_size_bytes: image.file_size_bytes ?? null,
        width: image.width ?? null,
        height: image.height ?? null,
      }));

      const categoryId = pick("category_id", null);
      const subcategoryId = pick("subcategory_id", null);
      const canonicalVariantObjects = (row.user_item_variants || [])
        .map((entry) => {
          if (!entry.variant?.id || !entry.variant?.name) return null;

          return {
            id: Number(entry.variant.id),
            name: entry.variant.name,
            canonical: true,
          };
        })
        .filter(Boolean);

      // Pending variant suggestions are still part of the collector's item.
      // Expose them through the same frontend contract as canonical variants so
      // detail/edit/list views never make a newly-entered variant disappear.
      const suggestedVariantObjects = (row.user_variant_suggestions || [])
        .filter((suggestion) => suggestion?.name && ["pending", "denied"].includes(suggestion.status))
        .map((suggestion) => ({
          id: null,
          suggestion_id: suggestion.id,
          name: suggestion.name,
          canonical: false,
        }));

      const variantObjects = [...canonicalVariantObjects, ...suggestedVariantObjects]
        .filter(
          (variant, index, all) =>
            all.findIndex(
              (candidate) =>
                String(candidate.name).trim().toLowerCase() ===
                String(variant.name).trim().toLowerCase()
            ) === index
        );

      // Keep the existing display contract (array of names) while also exposing
      // structured variants for CardForm / VariantsSection during edits.
      const variants = variantObjects.map((variant) => variant.name);

      return {
        id: row.id,
        user_id: row.user_id,
        item_id: row.item_id ?? null,
        name: pick("name", "Unknown"),
        brand: pick("brand", ""),
        set_name: pick("set_name", ""),
        subset: hasCanonicalItem
          ? canonical.subset ?? null
          : attributes.subset ?? null,
        year: pick("year"),
        year_end: pick("year_end", null),
        category_id: categoryId,
        category: categoryId
          ? categoriesMap[categoryId] ?? "Unknown"
          : "Unknown",
        subcategory_id: subcategoryId,
        subcategory: subcategoryId
          ? subcategoriesMap[subcategoryId] ?? "Unknown"
          : "Unknown",
        condition: row.condition || "Unknown",
        status: row.status || "Pending",
        images,
        image_url: images[0]?.image_url || null,
        collection_ids: collectionIdsByItem.get(String(row.id)) || [],
        variants,
        variations: variantObjects,
        notes: row.description ?? "",
        price_paid: row.price_paid,
        estimated_value: row.estimated_value,
        for_sale: row.for_sale ?? false,
        is_private: row.is_private ?? false,
        autographed: Boolean(attributes.autographed),
        graded: Boolean(
          attributes.graded ||
            attributes.grade ||
            attributes.grading_company ||
            attributes.cert_number
        ),
        grade: attributes.grade ?? null,
        grading_company: attributes.grading_company ?? null,
        card_number: hasCanonicalItem
          ? canonical.card_number ?? null
          : attributes.card_number ?? null,
        cert_number: attributes.cert_number ?? null,
        serial_number: attributes.serial_number ?? null,
        attributes,
        originalRow: row,
      };
    });
  } catch (err) {
    console.error("fetchUserCards error:", err);
    return [];
  }
};
